import { useState, useEffect, useCallback, useRef } from 'react';
import { getAllMockFeedItems } from '../data/mockFeedData';

// Toggle to false when backend endpoints are ready
const USE_MOCK_DATA = true;

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

/**
 * usePracticePulse — fetches and manages feed data from multiple sources.
 * Prioritizes AI alerts to always appear at top.
 */
export const usePracticePulse = (options = {}) => {
  const {
    autoRefresh = true,
    refreshInterval = 60000,
    filters = {},
    limit = 20,
  } = options;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const refreshTimerRef = useRef(null);
  const allItemsRef = useRef([]);

  const sortByPriority = useCallback((feedItems) => {
    const priorityOrder = { critical: 0, high: 1, normal: 2, low: 3 };

    return [...feedItems].sort((a, b) => {
      if (a.source === 'ai_alert' && b.source !== 'ai_alert') return -1;
      if (b.source === 'ai_alert' && a.source !== 'ai_alert') return 1;

      const priorityDiff = (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
      if (priorityDiff !== 0) return priorityDiff;

      return new Date(b.timestamp) - new Date(a.timestamp);
    });
  }, []);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let combined;

      if (USE_MOCK_DATA) {
        // Simulate network delay
        await new Promise((resolve) => setTimeout(resolve, 400));
        combined = getAllMockFeedItems();
      } else {
        const [activityRes, newsRes, alertsRes] = await Promise.all([
          fetch(`${API_BASE}/api/events/practice-activity`, {
            headers: { Accept: 'application/json' },
          }).then((r) => (r.ok ? r.json() : { items: [] })).catch(() => ({ items: [] })),
          fetch(`${API_BASE}/api/news/dental`, {
            headers: { Accept: 'application/json' },
          }).then((r) => (r.ok ? r.json() : { items: [] })).catch(() => ({ items: [] })),
          fetch(`${API_BASE}/api/ai/alerts/pending`, {
            headers: { Accept: 'application/json' },
          }).then((r) => (r.ok ? r.json() : { items: [] })).catch(() => ({ items: [] })),
        ]);

        combined = [
          ...(activityRes.items || []),
          ...(newsRes.items || []),
          ...(alertsRes.items || []).map((item) => ({
            ...item,
            source: 'ai_alert',
            priority: item.requiresAction ? 'critical' : 'high',
          })),
        ];
      }

      const sorted = sortByPriority(combined);

      // Apply filters
      let filtered = sorted;
      if (filters.sources?.length) {
        filtered = filtered.filter((item) => filters.sources.includes(item.source));
      }
      if (filters.categories?.length) {
        filtered = filtered.filter((item) => filters.categories.includes(item.category));
      }

      allItemsRef.current = filtered;
      const limited = filtered.slice(0, limit);
      setHasMore(filtered.length > limit);
      setItems(limited);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      setError(err.message);
      // Fallback to mock data on error
      const mockData = sortByPriority(getAllMockFeedItems());
      allItemsRef.current = mockData;
      setItems(mockData.slice(0, limit));
    } finally {
      setLoading(false);
    }
  }, [filters, limit, sortByPriority]);

  // Initial fetch
  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    refreshTimerRef.current = setInterval(() => {
      fetchFeed();
    }, refreshInterval);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, fetchFeed]);

  const refresh = useCallback(() => fetchFeed(), [fetchFeed]);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      setItems(allItemsRef.current);
      setHasMore(false);
    }
  }, [hasMore, loading]);

  const toggleLike = useCallback((itemId) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, isLiked: !item.isLiked, likeCount: (item.likeCount || 0) + (item.isLiked ? -1 : 1) }
          : item
      )
    );
  }, []);

  const toggleBookmark = useCallback((itemId) => {
    setItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, isBookmarked: !item.isBookmarked } : item))
    );
  }, []);

  const aiAlertCount = items.filter((item) => item.source === 'ai_alert').length;

  return {
    items,
    loading,
    error,
    lastUpdated,
    hasMore,
    hasAIAlerts: aiAlertCount > 0,
    aiAlertCount,
    refresh,
    loadMore,
    toggleLike,
    toggleBookmark,
  };
};
